import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateBoard, useBoardSlugSuggest } from '@/hooks/use-boards';
import { ProjectCombobox, type ProjectOption } from './project-combobox';
import { ProjectMembersPicker } from './project-members-picker';
import { useDepartments } from '@/hooks/use-organizations';
import { BOARD_TEMPLATES, suggestTemplateForDepartment } from '@hygge/shared';
import type { BoardTemplate, DepartmentWithRelations } from '@hygge/shared';
import { cn } from '@/lib/utils';
import { Building2, Check, Columns3, GitBranch, Bug, Target, LayoutDashboard, Settings2, Users } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useAuthStore } from '@/stores/auth';

const TEMPLATE_ICONS: Record<BoardTemplate, React.ElementType> = {
  sprint: Columns3,
  recruitment: GitBranch,
  sales: Target,
  'bug-tracking': Bug,
  kanban: LayoutDashboard,
  custom: Settings2,
};

interface CreateBoardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (boardId: string) => void;
}

export function CreateBoardDialog({ open, onOpenChange, onCreated }: CreateBoardDialogProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState<BoardTemplate>('sprint');
  const [project, setProject] = useState<ProjectOption | null>(null);
  // The project's people ticked to join the board: linking adds nobody (HW-983).
  const [projectMemberIds, setProjectMemberIds] = useState<string[]>([]);
  // Whoever creates the board owns it, so they are not offered as a Member.
  const myEmployeeId = useAuthStore((s) => s.user?.employee?.id);
  const creator = useMemo(() => new Set(myEmployeeId ? [myEmployeeId] : []), [myEmployeeId]);
  const pickerHintId = useId();
  const [departmentId, setDepartmentId] = useState<string>('');
  const [templateManual, setTemplateManual] = useState(false);
  const [visibility, setVisibility] = useState<'public' | 'members_only'>('members_only');

  const { data: slugSuggestion, isFetching: isSuggestingSlug } = useBoardSlugSuggest(slugManual ? '' : name);
  const { data: departments } = useDepartments();
  const createBoard = useCreateBoard();

  useEffect(() => {
    if (!slugManual && slugSuggestion?.slug) {
      setSlug(slugSuggestion.slug);
    }
  }, [slugSuggestion, slugManual]);

  // Auto-suggest template when department changes
  const handleDepartmentChange = useCallback((deptId: string) => {
    setDepartmentId(deptId);
    if (deptId && departments) {
      const dept = departments.find((d: DepartmentWithRelations) => d.id === deptId);
      if (dept && !templateManual) {
        const suggested = suggestTemplateForDepartment(dept.name);
        setTemplate(suggested);
      }
    }
  }, [departments, templateManual]);

  const handleTemplateChange = useCallback((t: BoardTemplate) => {
    setTemplateManual(true);
    setTemplate(t);
  }, []);

  const handleSlugChange = useCallback((value: string) => {
    setSlugManual(true);
    setSlug(value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10));
  }, []);

  const selectedDepartment = departments?.find((d: DepartmentWithRelations) => d.id === departmentId);

  const handleSubmit = useCallback(async () => {
    if (!name.trim() || !slug.trim()) return;

    try {
      const board = await createBoard.mutateAsync({
        name: name.trim(),
        slug,
        description: description.trim() || undefined,
        template,
        projectId: project?.id,
        projectMemberIds: project && projectMemberIds.length > 0 ? projectMemberIds : undefined,
        departmentId: departmentId || undefined,
        visibility,
      });
      onOpenChange(false);
      onCreated?.(board.id);
      setName('');
      setSlug('');
      setSlugManual(false);
      setDescription('');
      setTemplate('sprint');
      setProject(null);
      setProjectMemberIds([]);
      setDepartmentId('');
      setTemplateManual(false);
      setVisibility('members_only');
    } catch {
      // error handled by mutation
    }
  }, [name, slug, description, template, project, projectMemberIds, departmentId, visibility, createBoard, onOpenChange, onCreated]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Board</DialogTitle>
          <DialogDescription>
            Create a standalone board with pre-configured statuses for your workflow.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="board-name">Name</Label>
            <Input
              id="board-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Recruitment Board"
              autoFocus
            />
          </div>

          {/* Slug */}
          <div className="space-y-1.5">
            <Label htmlFor="board-slug">
              Key <span className="text-muted-foreground text-xs font-normal">(task prefix)</span>
            </Label>
            <Input
              id="board-slug"
              aria-invalid={!!name.trim() && !slug.trim() && (slugManual || !isSuggestingSlug)}
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="e.g. REC"
              className="font-mono uppercase"
              maxLength={10}
            />
            {/* The key is required — the Create button stays off without it — and
                saying so beats a button that silently will not press. Shown once
                a name is typed, so an empty form does not open on an error. */}
            {/* Not while a suggestion is on its way: the first keystroke would flash it. */}
            {name.trim() && !slug.trim() && (slugManual || !isSuggestingSlug) ? (
              <p className="text-[11px] text-destructive">
                A key is required: it prefixes every task on the board, e.g. REC-1.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Tasks will be named {slug || '???'}-1, {slug || '???'}-2, etc.
              </p>
            )}
          </div>

          {/* Project */}
          <div className="space-y-1.5">
            <Label htmlFor="board-project">
              Project <span className="text-muted-foreground text-xs font-normal">(optional)</span>
            </Label>
            <ProjectCombobox
              id="board-project"
              value={project}
              onChange={(next) => {
                // Another project has other people: the ticks start over.
                if (next?.id !== project?.id) setProjectMemberIds([]);
                setProject(next);
              }}
            />
            {project && (
              <div className="space-y-1.5 pt-1">
                <p id={pickerHintId} className="text-[11px] text-muted-foreground">
                  Linking the board gives the project's people no access. Tick who should join it as a Member.
                </p>
                <ProjectMembersPicker
                  projectId={project.id}
                  onBoard={creator}
                  onBoardNote="you, the board's owner"
                  labelledBy={pickerHintId}
                  value={projectMemberIds}
                  onChange={setProjectMemberIds}
                />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="board-desc">
              Description <span className="text-muted-foreground text-xs font-normal">(optional)</span>
            </Label>
            <Textarea
              id="board-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this board for?"
              rows={2}
              className="resize-none"
            />
          </div>

          {/* Department selector */}
          <div className="space-y-1.5">
            <Label>
              Department <span className="text-muted-foreground text-xs font-normal">(optional — auto-adds members)</span>
            </Label>
            <Select value={departmentId} onValueChange={handleDepartmentChange}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="No department — standalone board" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">No department</span>
                </SelectItem>
                {departments?.map((d: DepartmentWithRelations) => (
                  <SelectItem key={d.id} value={d.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{d.name}</span>
                      {d._count?.employees ? (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({d._count.employees})
                        </span>
                      ) : null}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDepartment && selectedDepartment._count?.employees ? (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" />
                {selectedDepartment._count.employees} employee{selectedDepartment._count.employees !== 1 ? 's' : ''} will be auto-added as members
              </p>
            ) : null}
          </div>

          {/* Template selector */}
          <div className="space-y-1.5">
            <Label>Template</Label>
            <div className="grid grid-cols-2 gap-2">
              {BOARD_TEMPLATES.map((t) => {
                const Icon = TEMPLATE_ICONS[t.key];
                const isSelected = template === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => handleTemplateChange(t.key)}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border p-3 text-left transition-all cursor-pointer',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                        : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50',
                    )}
                  >
                    <div className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                      isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                    )}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium">{t.name}</span>
                        {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                        {t.statuses.map((s) => s.name).join(' → ')}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Visibility — closed by default; going company-wide is a choice */}
          <div className="space-y-1.5">
            <Label>Visibility</Label>
            <Select
              value={visibility}
              onValueChange={(v) => setVisibility(v as 'public' | 'members_only')}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="members_only">Private</SelectItem>
                <SelectItem value="public">Public</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground leading-tight">
              {visibility === 'members_only'
                ? 'Only its members, its creator and admins can open it. Linking it to a project adds nobody.'
                : 'Everyone with access to Boards can read it. Editing still needs a board role.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !slug.trim() || createBoard.isPending}
          >
            {createBoard.isPending ? 'Creating...' : 'Create Board'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
